/**
 * Vistas derivadas do estado. Ficam aqui — e não no store — porque estado
 * derivado GUARDADO é estado que dessincroniza: bastava um evento esquecer-se
 * de recalcular a lista de canais para a sidebar mentir. Calculado no render,
 * mente é impossível.
 *
 * São funções puras sobre `AppState`, não hooks: dá para testá-las sem React e
 * dá para as compor dentro de um `useMemo` no sítio onde o custo importa.
 */

import type {
  Channel,
  ChannelType,
  Emoji,
  Member,
  PublicUser,
  Role,
  Server,
  Snowflake,
  VoiceState,
} from "@greenvoice/shared";
import {
  ALL_PERMISSIONS,
  deserialize,
  EVERYONE_RANK,
  Permission,
  type PermissionBits,
  type PermissionOverwrite,
  type ResolveContext,
  resolvePermissions,
} from "@greenvoice/shared";

import { idCompare, idGreater } from "../lib/ids";
import { type AppState, memberKey, muteKey } from "./store";

const TEXTUAL: ReadonlySet<ChannelType> = new Set<ChannelType>(["TEXT", "ANNOUNCEMENT", "DM", "GROUP_DM", "THREAD"]);

export const isTextual = (channel: Channel): boolean => TEXTUAL.has(channel.type);

/** Servidores por ordem de entrada (o id é monotónico, logo é a ordem certa). */
export function serverList(state: AppState): Server[] {
  return Object.values(state.servers).sort((a, b) => idCompare(a.id, b.id));
}

/** Canais de um servidor, sem categorias, na ordem em que se desenham. */
export function channelsOf(state: AppState, serverId: Snowflake): Channel[] {
  return Object.values(state.channels)
    .filter((c) => c.serverId === serverId)
    .sort((a, b) => a.position - b.position || idCompare(a.id, b.id));
}

export interface ChannelGroup {
  category: Channel | null;
  channels: Channel[];
}

/**
 * A sidebar: categorias na ordem, cada uma com os seus filhos, e os órfãos
 * (sem categoria) primeiro. Um canal cuja categoria já não existe continua a
 * aparecer — desaparecer da UI é sempre pior do que aparecer no sítio errado.
 *
 * Os TÓPICOS não entram. O `parentId` de um tópico aponta para o canal de onde
 * saiu, não para uma categoria — e a regra dos órfãos acima ("pai desconhecido
 * ⇒ mostra no topo") apanhava-os a todos e punha cada conversa lateral ao lado
 * do #geral. Quem os desenha é a linha do canal pai, por `threadsOf`.
 */
export function channelTree(state: AppState, serverId: Snowflake): ChannelGroup[] {
  const all = channelsOf(state, serverId).filter((c) => c.type !== "THREAD");
  const categories = all.filter((c) => c.type === "CATEGORY");
  const known = new Set(categories.map((c) => c.id));

  const orphans = all.filter((c) => c.type !== "CATEGORY" && (!c.parentId || !known.has(c.parentId)));
  const groups: ChannelGroup[] = orphans.length ? [{ category: null, channels: orphans }] : [];

  for (const category of categories) {
    groups.push({
      category,
      channels: all.filter((c) => c.type !== "CATEGORY" && c.parentId === category.id),
    });
  }
  return groups;
}

/**
 * Os tópicos de um canal, mais recente primeiro.
 *
 * Por id descendente e não por `position`: o snowflake é temporal, logo isto é
 * a mesma ordem que o servidor devolve sem precisar de outra coluna — e um
 * tópico não se arrasta na lista, por isso a `position` aqui nunca é editada.
 */
export function threadsOf(state: AppState, channelId: Snowflake): Channel[] {
  return Object.values(state.channels)
    .filter((c) => c.type === "THREAD" && c.parentId === channelId)
    .sort((a, b) => idCompare(b.id, a.id));
}

/**
 * O tópico que nasceu de uma mensagem, se existir.
 *
 * É o que deixa a linha da mensagem mostrar "ver tópico" em vez de oferecer
 * criar um segundo — o servidor recusa-o com um 409 (o `sourceMessageId` é
 * único), e um botão que só dá erro é pior do que botão nenhum.
 */
export function threadOfMessage(state: AppState, messageId: Snowflake): Channel | undefined {
  return Object.values(state.channels).find((c) => c.sourceMessageId === messageId);
}

/** Conversas directas e grupos, mais recente primeiro. */
export function directChannels(state: AppState): Channel[] {
  return Object.values(state.channels)
    .filter((c) => c.type === "DM" || c.type === "GROUP_DM")
    .sort((a, b) => idCompare(b.lastMessageId ?? b.id, a.lastMessageId ?? a.id));
}

/** Nome a mostrar num DM: quem está do outro lado. */
export function channelTitle(state: AppState, channel: Channel): string {
  if (channel.type === "DM") {
    const other = channel.recipientIds.find((id) => id !== state.self?.id);
    const user = other ? state.users[other] : undefined;
    return user ? (user.displayName ?? user.username) : channel.name || "Conversa";
  }
  if (channel.type === "GROUP_DM" && !channel.name) {
    const names = channel.recipientIds
      .filter((id) => id !== state.self?.id)
      .map((id) => state.users[id])
      .filter((u): u is PublicUser => Boolean(u))
      .map((u) => u.displayName ?? u.username);
    return names.join(", ") || "Grupo";
  }
  return channel.name;
}

// ── permissões ───────────────────────────────────────────────

type ChannelOverwrites = NonNullable<ResolveContext["channelOverwrites"]>;

function overwritesOf(channel: Channel, everyoneRoleId: Snowflake | undefined, userId: Snowflake): ChannelOverwrites {
  const roles = new Map<string, PermissionOverwrite>();
  let everyone: PermissionOverwrite | undefined;
  let member: PermissionOverwrite | undefined;

  for (const o of channel.overwrites) {
    const pair = { allow: deserialize(o.allow), deny: deserialize(o.deny) };
    if (o.targetType === "MEMBER") {
      if (o.targetId === userId) member = pair;
      continue;
    }
    if (o.targetId === everyoneRoleId) everyone = pair;
    else roles.set(o.targetId, pair);
  }

  // Espalhado condicionalmente: com `exactOptionalPropertyTypes`, uma chave
  // presente a valer `undefined` NÃO é o mesmo que chave ausente.
  return {
    roles,
    ...(everyone ? { everyone } : {}),
    ...(member ? { member } : {}),
  };
}

/**
 * As permissões efectivas do próprio, num servidor ou dentro de um canal.
 * Corre a MESMA função que a API usa para autorizar — é isso que evita a
 * classe de bug "o botão aparece e a API responde 403".
 */
export function selfPermissions(state: AppState, serverId: Snowflake | null, channel?: Channel | null): PermissionBits {
  const self = state.self;
  if (!self) return 0n;
  // Fora de um servidor (DM) não há hierarquia: pode tudo o que é de canal.
  if (!serverId) return ALL_PERMISSIONS;

  const server = state.servers[serverId];
  if (!server) return 0n;
  if (server.ownerId === self.id) return ALL_PERMISSIONS;

  const member = state.members[memberKey(serverId, self.id)];
  const serverRoles = Object.values(state.roles).filter((r) => r.serverId === serverId);
  const everyoneRole = serverRoles.find((r) => r.isDefault);
  const memberRoles = (member?.roleIds ?? [])
    .map((id) => state.roles[id])
    .filter((r): r is Role => r !== undefined && !r.isDefault)
    .map((r) => ({ id: r.id, rank: r.rank, permissions: deserialize(r.permissions) }));

  const timedOut = member?.timeoutUntil ? Date.parse(member.timeoutUntil) > Date.now() : false;

  return resolvePermissions({
    isOwner: false,
    everyonePermissions: deserialize(everyoneRole?.permissions),
    roles: memberRoles,
    isTimedOut: timedOut,
    ...(channel ? { channelOverwrites: overwritesOf(channel, everyoneRole?.id, self.id) } : {}),
  });
}

export function can(permissions: PermissionBits, required: PermissionBits): boolean {
  if (permissions & Permission.ADMINISTRATOR) return true;
  return (permissions & required) === required;
}

// ── membros ──────────────────────────────────────────────────

export interface MemberGroup {
  /** null = "Online"/"Ausentes", o grupo sem role destacada. */
  role: Role | null;
  label: string;
  members: Member[];
}

const memberName = (member: Member): string => member.nickname ?? member.user.displayName ?? member.user.username;

const byName = (a: Member, b: Member): number =>
  memberName(a).localeCompare(memberName(b), "pt-PT", { sensitivity: "base" });

/**
 * Lista de membros agrupada como no Discord: roles com `hoist` em secções
 * próprias por ordem de hierarquia, o resto em "Online", e os offline no fim.
 * Um membro aparece SÓ na role destacada mais alta que tem.
 */
export function memberGroups(state: AppState, serverId: Snowflake): MemberGroup[] {
  // O `member.user` gravado no Ready é um snapshot CONGELADO: a presença muda
  // por `PresenceUpdate`, que só toca em `state.users`. Se lêssemos `m.user`
  // aqui, a lista dizia "offline" para sempre — a pessoa aparece online no
  // painel dela e offline na barra lateral. Resolve-se o utilizador vivo.
  const members = Object.values(state.members)
    .filter((m) => m.serverId === serverId)
    .map((m) => {
      const live = state.users[m.user.id];
      return live && live !== m.user ? { ...m, user: live } : m;
    });
  const hoisted = Object.values(state.roles)
    .filter((r) => r.serverId === serverId && r.hoist && !r.isDefault)
    .sort((a, b) => a.rank - b.rank || idCompare(a.id, b.id));

  const groups: MemberGroup[] = [];
  const taken = new Set<Snowflake>();

  for (const role of hoisted) {
    const bucket = members
      .filter((m) => !taken.has(m.user.id) && m.roleIds.includes(role.id) && isOnline(m.user))
      .sort(byName);
    if (!bucket.length) continue;
    for (const m of bucket) taken.add(m.user.id);
    groups.push({ role, label: role.name, members: bucket });
  }

  const online = members.filter((m) => !taken.has(m.user.id) && isOnline(m.user)).sort(byName);
  if (online.length) groups.push({ role: null, label: "Online", members: online });

  const offline = members.filter((m) => !isOnline(m.user)).sort(byName);
  if (offline.length) groups.push({ role: null, label: "Offline", members: offline });

  return groups;
}

export const isOnline = (user: PublicUser): boolean => user.status !== "OFFLINE" && user.status !== "INVISIBLE";

/** A cor do nome = a role destacada mais alta com cor. */
export function memberColor(state: AppState, member: Member | undefined): string | null {
  if (!member) return null;
  const roles = member.roleIds
    .map((id) => state.roles[id])
    .filter((r): r is Role => r !== undefined && Boolean(r.color))
    .sort((a, b) => a.rank - b.rank);
  return roles[0]?.color ?? null;
}

export function highestRankOf(state: AppState, serverId: Snowflake, userId: Snowflake): number {
  const member = state.members[memberKey(serverId, userId)];
  if (!member) return EVERYONE_RANK;
  return member.roleIds.reduce((min, id) => {
    const role = state.roles[id];
    return role && role.rank < min ? role.rank : min;
  }, EVERYONE_RANK);
}

// ── voz ──────────────────────────────────────────────────────

export function voiceParticipants(state: AppState, channelId: Snowflake): VoiceState[] {
  return Object.values(state.voiceStates)
    .filter((v) => v.channelId === channelId)
    .sort((a, b) => Date.parse(a.joinedAt) - Date.parse(b.joinedAt));
}

export function selfVoiceState(state: AppState): VoiceState | undefined {
  return state.self ? state.voiceStates[state.self.id] : undefined;
}

/** Os quatro booleanos que decidem os sinais de voz. `VoiceState` serve-o. */
export interface VoiceMarks {
  selfMute: boolean;
  selfDeaf: boolean;
  serverMute: boolean;
  serverDeaf: boolean;
}

/** O que o MOTOR (o browser) sabe sobre o meu microfone, agora. */
export interface EngineVoice {
  /** Há uma ligação de voz minha a correr — qualquer que seja o sítio. */
  connected: boolean;
  micEnabled: boolean;
  deafened: boolean;
}

/**
 * O MEU estado de voz — do motor local, não do eco do servidor.
 *
 * `voiceStates[eu]` só existe porque o servidor mo mandou de volta, e há três
 * situações em que ele nunca manda: o huddle de corredor (não tem `voiceState`
 * na API — o `sync` desiste com `voice === null`), a fracção de segundo entre
 * carregar no botão e o evento chegar, e um `updateState` que falhou em
 * silêncio. Nessas três, o ícone dizia que eu estava a falar enquanto o meu
 * microfone estava fechado — a mentira mais cara da app, porque é a que me
 * convence a falar.
 *
 * O browser é a autoridade sobre o MEU microfone (é ele que o tem na mão); o
 * servidor continua a ser a autoridade sobre o silêncio IMPOSTO, que o motor
 * local não pode conhecer. Por isso metade vem de cada lado, e não uma fonte a
 * ganhar à outra. Fora de uma chamada não há motor: vale o que o servidor diz.
 */
export function ownVoiceMarks(echo: VoiceMarks | undefined, engine: EngineVoice): VoiceMarks | undefined {
  if (!engine.connected) return echo;
  return {
    selfMute: !engine.micEnabled,
    selfDeaf: engine.deafened,
    serverMute: echo?.serverMute ?? false,
    serverDeaf: echo?.serverDeaf ?? false,
  };
}

// ── silenciado ───────────────────────────────────────────────

/**
 * Este servidor está calado?
 *
 * O dicionário só guarda silêncios vivos (o Ready filtra-os e o relógio do store
 * apaga os que expiram), por isso a presença da chave basta — não se volta a
 * comparar a data aqui.
 */
export function isServerMuted(state: AppState, serverId: Snowflake): boolean {
  return state.mutes[muteKey("SERVER", serverId)] !== undefined;
}

/**
 * Este canal está calado — por si, ou por o servidor inteiro estar?
 *
 * As duas coisas calam. Quem silencia um servidor não tem de repetir o gesto
 * canal a canal, e é essa herança que faz o silêncio de servidor valer a pena.
 */
export function isChannelMuted(state: AppState, channel: Channel): boolean {
  if (state.mutes[muteKey("CHANNEL", channel.id)] !== undefined) return true;
  return channel.serverId !== null && isServerMuted(state, channel.serverId);
}

/** O silêncio que vale para este canal, para dizer «até quando» a quem pergunta. */
export function muteOf(state: AppState, channel: Channel) {
  return (
    state.mutes[muteKey("CHANNEL", channel.id)] ??
    (channel.serverId ? state.mutes[muteKey("SERVER", channel.serverId)] : undefined)
  );
}

// ── emojis personalizados ────────────────────────────────────

/**
 * Os emojis de um servidor, por ordem alfabética.
 *
 * O dicionário do store é global por id (é assim que uma mensagem resolve o
 * `<:festa:123>` sem saber de que servidor veio); esta é a vista contrária, a
 * que a grelha das definições e o seletor precisam. Duzentos emojis por
 * servidor é o tecto, por isso o varrimento é barato — e o resultado é novo a
 * cada chamada, o que obriga quem o usa num render a passar por `useMemo`.
 *
 * Pede o dicionário e não o `AppState` inteiro, ao contrário dos vizinhos: é o
 * que deixa um componente subscrever só `s.emojis` em vez do estado todo. Um
 * `AppState` continua a servir de argumento — tem o campo.
 */
export function emojisOf(state: Pick<AppState, "emojis">, serverId: Snowflake): Emoji[] {
  return Object.values(state.emojis)
    .filter((emoji) => emoji.serverId === serverId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ── por ler ──────────────────────────────────────────────────

export function isUnread(state: AppState, channel: Channel): boolean {
  if (!channel.lastMessageId) return false;
  if (!isTextual(channel)) return false;
  // Silenciado esconde o PONTINHO, e não o contador de menções logo abaixo:
  // calar o barulho de fundo não é ignorar quem chamou pelo nome. O número fica
  // — é o que se quer encontrar quando se abre a aplicação de propósito.
  if (isChannelMuted(state, channel)) return false;
  const read = state.readStates[channel.id];
  return idGreater(channel.lastMessageId, read?.lastReadMessageId ?? null);
}

export function mentionsOf(state: AppState, channelId: Snowflake): number {
  return state.readStates[channelId]?.mentionCount ?? 0;
}

export function serverHasUnread(state: AppState, serverId: Snowflake): boolean {
  return channelsOf(state, serverId).some((c) => isUnread(state, c));
}

export function serverMentions(state: AppState, serverId: Snowflake): number {
  return channelsOf(state, serverId).reduce((sum, c) => sum + mentionsOf(state, c.id), 0);
}

// ── a escrever ───────────────────────────────────────────────

/** "A Ana está a escrever…" — já em PT-PT, com o plural certo. */
export function typingText(state: AppState, channelId: Snowflake): string | null {
  const now = Date.now();
  const names = Object.entries(state.typing[channelId] ?? {})
    .filter(([userId, expiry]) => expiry > now && userId !== state.self?.id)
    .map(([userId]) => {
      const serverId = state.channels[channelId]?.serverId;
      const member = serverId ? state.members[memberKey(serverId, userId)] : undefined;
      const user = state.users[userId];
      return member?.nickname ?? user?.displayName ?? user?.username ?? "Alguém";
    });

  if (!names.length) return null;
  if (names.length === 1) return `${names[0]} está escrevendo…`;
  if (names.length === 2) return `${names[0]} e ${names[1]} estão escrevendo…`;
  if (names.length === 3) return `${names[0]}, ${names[1]} e ${names[2]} estão escrevendo…`;
  return "Várias pessoas estão escrevendo…";
}
